/**
 * Document Imports Vuex Mutations
 * Synchronous state changes for the document import feature.
 */

import { WIZARD_STEPS } from '../config/wizardSteps';

export default {
  // === Dialog State ===
  SET_UPLOAD_DIALOG_OPEN(state, value) {
    state.uploadDialogOpen = value;
  },

  // === Wizard State ===
  SET_WIZARD_STEP(state, step) {
    state.wizardStep = step;
  },

  NEXT_WIZARD_STEP(state) {
    if (state.wizardStep < WIZARD_STEPS.SUCCESS) {
      state.wizardStep++;
    }
  },

  PREV_WIZARD_STEP(state) {
    if (state.wizardStep > WIZARD_STEPS.CONFIGURE) {
      state.wizardStep--;
    }
  },

  // === Scope Selection ===
  SET_SCOPE_TYPE(state, value) {
    state.scopeType = value;
    // Clear the opposite selection when scope type changes
    if (value === 'Facility') {
      state.selectedFacilityGroupId = null;
    } else {
      state.selectedFacilityId = null;
    }
  },

  SET_SELECTED_FACILITY_ID(state, value) {
    state.selectedFacilityId = value;
    if (value) {
      state.selectedFacilityGroupId = null;
      state.scopeType = 'Facility';
    }
  },

  SET_SELECTED_FACILITY_GROUP_ID(state, value) {
    state.selectedFacilityGroupId = value;
    if (value) {
      state.selectedFacilityId = null;
      state.scopeType = 'FacilityGroup';
    }
  },

  // === Upload Configuration ===
  SET_SELECTED_DOCUMENT_TYPE_ID(state, value) {
    state.selectedDocumentTypeId = value;
  },

  // === Pending Files Management ===
  SET_PENDING_FILES(state, files) {
    state.pendingFiles = files;
  },

  ADD_PENDING_FILES(state, files) {
    state.pendingFiles = [...state.pendingFiles, ...files];
  },

  UPDATE_PENDING_FILE(state, { index, updates }) {
    if (state.pendingFiles[index]) {
      state.pendingFiles[index] = { ...state.pendingFiles[index], ...updates };
      // Trigger reactivity
      state.pendingFiles = [...state.pendingFiles];
    }
  },

  REMOVE_PENDING_FILE(state, index) {
    state.pendingFiles.splice(index, 1);
    state.pendingFiles = [...state.pendingFiles];
  },

  CLEAR_PENDING_FILES(state) {
    state.pendingFiles = [];
    state.selectedFileIndexes = [];
  },

  // === Selected Files (for bulk operations) ===
  SET_SELECTED_FILE_INDEXES(state, indexes) {
    state.selectedFileIndexes = indexes;
  },

  TOGGLE_FILE_SELECTION(state, index) {
    const idx = state.selectedFileIndexes.indexOf(index);
    if (idx > -1) {
      state.selectedFileIndexes.splice(idx, 1);
    } else {
      state.selectedFileIndexes.push(index);
    }
    state.selectedFileIndexes = [...state.selectedFileIndexes];
  },

  SELECT_ALL_FILES(state) {
    state.selectedFileIndexes = state.pendingFiles.map((_, idx) => idx);
  },

  DESELECT_ALL_FILES(state) {
    state.selectedFileIndexes = [];
  },

  // === Existing Documents ===
  SET_DOCUMENTS(state, documents) {
    state.documents = documents;
  },

  SET_DOCUMENTS_TOTAL(state, total) {
    state.documentsTotal = total;
  },

  // === Table Options ===
  SET_TABLE_OPTIONS(state, options) {
    state.tableOptions = { ...state.tableOptions, ...options };
  },

  // === Document Types ===
  SET_DOCUMENT_TYPES(state, types) {
    state.documentTypes = types;
    state.documentTypesLoaded = true;
  },

  // === Loading States ===
  SET_LOADING(state, value) {
    state.loading = value;
  },

  SET_UPLOADING(state, value) {
    state.uploading = value;
  },

  SET_IMPORTING(state, value) {
    state.importing = value;
  },

  SET_DELETING(state, value) {
    state.deleting = value;
  },

  // === Error State ===
  SET_ERROR(state, error) {
    state.error = error;
  },

  CLEAR_ERROR(state) {
    state.error = null;
  },

  // === Import Result ===
  SET_IMPORT_RESULT(state, result) {
    state.importResult = result;
  },

  CLEAR_IMPORT_RESULT(state) {
    state.importResult = null;
  },

  // === Reset State ===
  RESET_UPLOAD_STATE(state) {
    state.pendingFiles = [];
    state.selectedFileIndexes = [];
    state.selectedDocumentTypeId = null;
    state.uploading = false;
    state.importing = false;
    state.error = null;
    state.importResult = null;
  },

  // === Reset Wizard ===
  RESET_WIZARD(state) {
    state.wizardStep = WIZARD_STEPS.CONFIGURE;
    state.pendingFiles = [];
    state.selectedFileIndexes = [];
    state.selectedDocumentTypeId = null;
    state.uploading = false;
    state.importing = false;
    state.error = null;
    state.importResult = null;
  },
};
